import { TypedDocumentNode, gql } from '@apollo/client';
import { useState } from 'react';

import { DeliverableItemStatus } from '@sorare/core/src/__generated__/globalTypes';
import { DeliverableItemOrderFooter } from '@sorare/core/src/components/rewards/deliverableItem/DeliverableItemOrderFooter';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';

import DeliverableItemOrderFormDialog, {
  deliverableItemOrderFragment,
} from 'components/rewards/deliverableItem/DeliverableItemOrderFormDialog';

import {
  DeliverableItemFooter_DeliverableItem,
  UnClaimedDeliverableItemOrder,
  UnClaimedDeliverableItemOrderVariables,
} from './__generated__/index.graphql';

const UNCLAIMED_DELIVERABLE_ITEM_ORDER_QUERY = gql`
  query UnClaimedDeliverableItemOrder($sport: Sport!, $deliverableItemId: ID) {
    currentUser {
      slug
      unclaimedDeliverableItemOrders(
        sport: $sport
        deliverableItemId: $deliverableItemId
      ) {
        id
        aasmState
        deliverableItem {
          slug
          id
        }
        ...DeliverableItemOrderFormDialog_deliverableItemOrderFragment
      }
    }
  }
  ${deliverableItemOrderFragment}
` as TypedDocumentNode<
  UnClaimedDeliverableItemOrder,
  UnClaimedDeliverableItemOrderVariables
>;

type Props = {
  deliverableItem: DeliverableItemFooter_DeliverableItem;
  onSkip?: () => void;
};

const DeliverableItemFooter = ({ deliverableItem, onSkip }: Props) => {
  const [formOpen, setFormOpen] = useState(false);
  const [hasOrdered, setHasOrdered] = useState(false);

  const { data } = useQuery(UNCLAIMED_DELIVERABLE_ITEM_ORDER_QUERY, {
    variables: {
      deliverableItemId: deliverableItem.id,
      sport: deliverableItem.sport,
    },
  });

  const deliverableItemOrder =
    data?.currentUser?.unclaimedDeliverableItemOrders[0];

  if (!deliverableItemOrder || hasOrdered) return null;

  const hasFinishOrder =
    !formOpen && deliverableItemOrder.aasmState !== DeliverableItemStatus.READY;

  if (hasFinishOrder) return null;

  return (
    <>
      <DeliverableItemOrderFooter
        onSkip={onSkip}
        onClick={() => {
          setFormOpen(true);
        }}
      />
      {formOpen && (
        <DeliverableItemOrderFormDialog
          deliverableItemOrder={deliverableItemOrder}
          open
          onCancel={() => {
            setFormOpen(false);
          }}
          onDone={() => {
            setHasOrdered(true);
            setFormOpen(false);
          }}
        />
      )}
    </>
  );
};

DeliverableItemFooter.fragments = {
  deliverableItem: gql`
    fragment DeliverableItemFooter_DeliverableItem on DeliverableItemInterface {
      slug
      id
      sport
    }
  ` as TypedDocumentNode<DeliverableItemFooter_DeliverableItem>,
};

export default DeliverableItemFooter;
